import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import classnames from "classnames";
import { fabric } from "fabric";
import renderHtml from "react-render-html";
import * as diagramActions from "../../actions/diagramActions";
import { uppercaseUnderscore } from "../../utils/stringConversion";
import { hexToRGB } from "../../utils/colorConversion";
import {
  themeIdentifier,
  pitchIdentifier,
} from "../../utils/diagramIdentifierName";

import PageContainer from "../Global/Containers/PageContainer";
import ModalContainer from "../Global/Modals/ModalContainer";
import ConfirmModal from "../Global/Modals/ConfirmModal";
import DiagramCreatorOptions from "./DiagramCreatorOptions";
import DiagramCreatorHeader from "./DiagramCreatorHeader";
import DiagramCreatorShapes from "./Tools/DiagramCreatorShapes";
import DiagramCreatorEquipment from "./Tools/DiagramCreatorEquipment";
import DiagramCreatorPlayers from "./Tools/DiagramCreatorPlayers";
import DiagramCreatorLines from "./Tools/DiagramCreatorLines";
import DiagramCreatorText from "./Tools/DiagramCreatorText";
import CreateNewModal from "../Global/Modals/CreateNewModal";
import * as themes from "./Themes";
import * as pitches from "./Pitches";
import * as items from "./Items";
import * as colors from "./ItemColors";
import drawEquipment from "./Items/Equipment";
import drawPlayer from "./Items/Player";
import Rectangle from "./Items/Rectangle";
import Circle from "./Items/Circle";
// import LineWithArrow from './Items/LineWithArrow';
import LegacyLineWithArrow from "./Items/LegacyLineWithArrow";
import Line from "./Items/Line";
import LegacyLine from "./Items/LegacyLine";
import WavyLine from "./Items/WavyLine";
import Text from "./Items/Text";
import staticUrl from "../../utils/staticPath";

let fabricCanvas; // Globally accessible fabricCanvas instance
const shapeCornerSize = 8;
const strokeDashArray = [5, 5];
const strokeWidth = 2;
const objectPadding = 12;

class DiagramCreatorPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      content: { ...props.diagram },
      loading: false,
      theme: null,
      themeId: 0,
      title: ``,
      pitch: null,
      parentPitch: null,
      pitchBackgroundObject: null,
      iconSize: `S`,
      shapeIconColor: colors.YELLOW,
      equipmentIconColor: colors.YELLOW,
      playerIconColor: colors.GREEN,
      lineIconColor: colors.BLACK,
      activeItem: null,
      activeLabel: ``,
      labelInputActive: false,
      clearAllModalOpen: false,
      saveModalOpen: false,
      dragging: false,
      validDrop: false,
      activeTool: null,
      borderColor: null,
      gridColor: null,
      gridObject: null,
      gridShowing: false,
      lineAnchorStart: null,
      lineAnchorEnd: null,
      lineAnchorBend: null,
      editingText: false,
      watermarkObj: {},
    };
  }

  componentDidMount() {
    this.initFabric();
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.organisation.primaryLogo !== this.props.organisation.primaryLogo
    ) {
      if (this.state.pitch !== null) {
        this.addWatermark(nextProps.organisation.primaryLogo);
      }
    }

    // Loading a saved diagram
    if (this.props.diagram.id !== nextProps.diagram.id) {
      // The 'saved' key is not returned via the API, instead added via the reducer to allow the canvasToImage function to run below
      // We don't want the following if statement to run when saving a diagram as it will redraw everything on the canvas and causes issues with
      // saving the canvas to image as shapes have transparency and it would overlay 2 shapes on top of each other
      if (!(`saved` in nextProps.diagram)) {
        const themeObjectRef = themeIdentifier(
          nextProps.diagram.pitch_theme.id,
        );
        const theme = themes[themeObjectRef];
        const pitchObjectRef = pitchIdentifier(
          nextProps.diagram.pitch_image.id,
        );
        const pitch = pitches[pitchObjectRef];

        this.setState(
          {
            content: nextProps.diagram,
            title: nextProps.diagram.title,
            iconSize: pitch.iconSize,
            theme,
            playerIconColor: theme.playerIconColor,
            borderColor: theme.borderColor,
            // iconSize, theme, playerIconColor and borderColor are already set as part of the changeTheme() & changePitch() functions called in this setState callback,
            // but we need them to be set before we call loadSavedObjects() in order to draw our objects correctly
          },
          () => {
            this.changeTheme(themeObjectRef, nextProps.diagram.pitch_theme.id);
            this.changePitch(pitchObjectRef, nextProps.diagram.pitch_image.id);
            this.loadSavedObjects(nextProps.diagram.pitch_data.objects);

            // If the URL contains the 'progression=true' query param
            if (nextProps.location.query.progression === `true`) {
              this.addProgression();
            }
          },
        );
      }
    }
  }

  addProgression = () => {
    const content = { ...this.state.content };
    delete content.id;
    content.title = ``;
    this.setState({ content, title: `` });
  };

  initFabric = () => {
    fabricCanvas = new fabric.Canvas(`canvas`, {
      height: 563,
      width: 1000,
      targetFindTolerance: objectPadding, // Needed alongside the 'padding' attribute for lines to be easily selectable https://stackoverflow.com/questions/48564274/fabricjs-line-selection-with-perpixeltargetfind-varies-depending-on-angle-of-lin/48565580#48565580
      perPixelTargetFind: true, // To prevent lines (paths) having a selectable rectangle drawn around it and instead only have it selectable on direct click
      preserveObjectStacking: true,
      allowTouchScrolling: true,
    });
    this.addEventListeners();
  };

  loadSavedObjects = (pitchObjects) => {
    pitchObjects.map((object) => {
      if (object.customProps) {
        const { left, top } = object;
        let itemType;
        let itemCategory;

        // If we're loading an object which differs depending on the theme selected
        // (teamsheet players for example which are either circles or triangles depending on the theme)
        if (object.customProps.tcm || object.customProps.ussf) {
          itemType = uppercaseUnderscore(
            object.customProps[this.state.theme.class].type,
          );
          itemCategory = object.customProps[this.state.theme.class].category;
        } else {
          itemType = uppercaseUnderscore(object.customProps.type);
          itemCategory = object.customProps.category;
        }

        switch (itemCategory) {
          case `shapes`: {
            const { fill, height, width, scaleX, scaleY, rotateAngle } = object;
            return this.addSavedShapeToCanvas(
              items[itemType],
              left,
              top,
              height,
              width,
              fill,
              scaleX,
              scaleY,
              rotateAngle,
            );
          }
          case `lines`: {
            const { stroke, x1, y1, x2, y2 } = object;
            let lineType = itemType;
            if (itemType === `LINE_WITH_ARROW`) {
              lineType = `LEGACY_LINE_WITH_ARROW`;
            } else if (itemType === `DASHED_LINE_WITH_ARROW`) {
              lineType = `LEGACY_DASHED_LINE_WITH_ARROW`;
            } else if (itemType === `DASHED_LINE`) {
              lineType = `LEGACY_DASHED_LINE`;
            }
            return this.addSavedLineToCanvas(
              items[lineType],
              left,
              top,
              stroke,
              x1,
              y1,
              x2,
              y2,
            );
          }
          case `paths`: {
            const { stroke, path } = object;
            return this.addSavedPathToCanvas(
              items[itemType],
              left,
              top,
              stroke,
              path,
            );
          }
          case `equipment`: {
            const { iconFill, rotateAngle } = object;
            return this.addDraggableCanvasItem(
              null,
              items[itemType],
              left,
              top,
              iconFill,
              rotateAngle,
            );
          }
          case `players`: {
            const { iconFill, rotateAngle, playerText } = object;
            return this.addDraggableCanvasItem(
              null,
              items[itemType],
              left,
              top,
              iconFill,
              rotateAngle,
              playerText,
            );
          }
          case `text`: {
            const { width, rotateAngle, text, textAlign, fontSize } = object;
            return this.addSavedTextToCanvas(
              items[itemType],
              left,
              top,
              width,
              rotateAngle,
              text,
              textAlign,
              fontSize,
            );
          }
          // no default
        }
      }
      return false;
    });
  };

  addSavedTextToCanvas = (
    item,
    left,
    top,
    width,
    rotateAngle,
    text,
    textAlign,
    fontSize,
  ) => {
    const textbox = this.drawTextBox(item);
    textbox.set({ text, width, left, top, textAlign, fontSize });
    this.loadedRotate(textbox, rotateAngle);
    fabricCanvas.add(textbox).renderAll();
  };

  addSavedShapeToCanvas = (
    item,
    left,
    top,
    height,
    width,
    fill,
    scaleX,
    scaleY,
    rotateAngle,
  ) => {
    const shape = this.selectShape(item, left, top, fill);
    this.addShapeToCanvas(shape);
    this.loadedShapeSize(shape, height, width, scaleX, scaleY);
    this.loadedRotate(shape, rotateAngle);
    this.loadedShapePosition(shape, left, top);
  };

  loadedShapeSize = (shape, height, width, scaleX, scaleY) => {
    const shapeHeight = height * scaleY;
    const shapeWidth = width * scaleX;

    if (shape.customProps.type === `circle`) {
      shape
        .set({ rx: Math.abs(shapeWidth / 2), ry: Math.abs(shapeHeight / 2) })
        .setCoords();
    } else {
      shape.set({ width: shapeWidth, height: shapeHeight }).setCoords();
    }
  };

  loadedShapePosition = (shape, left, top) => {
    shape.set({ left, top }).setCoords();
  };

  addSavedLineToCanvas = (item, left, top, stroke, x1, y1, x2, y2) => {
    const points = [x1, y1, x2, y2];
    const line = this.selectLine(item, points, stroke, true);
    line.set({ left, top });
    this.lineCoordinatesForAnchors(line);
    fabricCanvas.add(line).renderAll();
  };

  addSavedPathToCanvas = (item, left, top, stroke, path) => {
    const line = this.selectLine(item, path, stroke);
    line.set({ left, top });
    fabricCanvas.add(line).renderAll();
  };

  changeTheme = (themeObjectRef, themeId) => {
    const theme = themes[themeObjectRef];
    this.setState(
      {
        theme,
        themeId,
        playerIconColor: theme.playerIconColor,
        borderColor: theme.borderColor,
        gridColor: theme.gridColor,
      },
      () => {
        this.addLineAnchors();
        fabricCanvas.selectionColor = hexToRGB(this.state.borderColor, 0.1);
        fabricCanvas.selectionBorderColor = this.state.borderColor;
        fabric.Group.prototype.hasControls = false; // Prevent multi-select group from having transform/rotate controls
        fabric.Group.prototype.borderColor = this.state.borderColor;
        fabric.Group.prototype.borderOpacityWhenMoving = 0.5;
        fabric.Group.prototype.perPixelTargetFind = true;
      },
    );
  };

  handleChangeThemeClick = (e, option) => {
    e.preventDefault();
    const themeObjectRef = themeIdentifier(option.value);
    this.changeTheme(themeObjectRef, option.value);
  };

  addWatermark = (watermarkSVG) => {
    try {
      // Grab pitch from state and watermarkSVG from passed arguement
      const { pitch } = this.state;
      const watermark = watermarkSVG || this.props.organisation.primaryLogo;

      // WATERMARK POSITIONING: Find area between pitch diagram and canvas
      const spaceWidth = Math.round((fabricCanvas.getWidth() - pitch.width) / 2);
      const spaceHeight = Math.round(
        (fabricCanvas.getHeight() - pitch.height) / 2,
      );

      // WATERMARK SIZING: Watermark size and 'padding/spacing' set depending on chosen pitch diagram
      let stringToSVG = renderHtml(watermark);
      // Checking to see if an array is returned (often due to extra spaces in SVG)
      if (stringToSVG.length > 0) {
        stringToSVG = stringToSVG.filter(obj => obj.type === `svg`)[0];
      }

      const originalWatermarkWidth = Number(stringToSVG.props.width);
      const originalWatermarkHeight = Number(stringToSVG.props.height);
      const aspectRatio = originalWatermarkWidth / originalWatermarkHeight;

      let logoPadding;
      let logoSize;
      switch (pitch.iconSize) {
        case `S`:
          logoSize = 40;
          logoPadding = 8;
          break;
        case `M`:
          logoSize = 50;
          logoPadding = 10;
          break;
        case `L`:
          logoSize = 70;
          logoPadding = 12;
          break;
        // no default
      }

      // Use the larger side of the given SVG to generate logoSize
      let logoWidth;
      let logoHeight;
      if (originalWatermarkWidth > originalWatermarkHeight) {
        logoWidth = logoSize;
        // Adjusting width via aspect ratio uses a second divide
        logoHeight = Math.round(logoSize / aspectRatio);
      } else {
        logoHeight = logoSize;
        // Adjusting height via aspect ratio uses multiplication
        logoWidth = Math.round(logoSize * aspectRatio);
      }

      // WATERMARK COLOUR: Watermark colour depending on chosen pitch theme
      const logoColour = this.state.themeId === 2 ? colors.WHITE : (this.props.organisation.primaryColour || colors.BLACK);

      // WATERMARK FABRIC: Build watermark fabric object
      fabric.loadSVGFromString(watermark, (objects, options) => {
        const watermarkObj = fabric.util.groupSVGElements(objects, options);
        watermarkObj
          .set({
            left: (spaceWidth + pitch.width) - (logoWidth + logoPadding),
            top: (spaceHeight + pitch.height) - (logoHeight + logoPadding),
            selectable: false,
            evented: false,
            fill: logoColour,
            opacity: 0.3,
          })
          .setCoords();
        watermarkObj.scaleToWidth(logoWidth);
        fabricCanvas.add(watermarkObj).renderAll();
        this.setState({ watermarkObj });
      });
    } catch (error) {
      // The try block above will fail if no watermarkSVG function argument is passed and if 
      // the this.props.organisation.primaryLogo doesn't exist. All of the subsequent code relies
      // on watermark being an SVG so if it isn't then things break. Because the watermark isn't
      // crucial to the functionality of the page, if we can't set it we don't need to handle it.
      // Note that this should only ever really happen if the organisation API returns after the user 
      // has selected their theme and pitch.
    }
  };

  changePitch = (pitchObjectRef, pitchId) => {
    const pitch = pitches[pitchObjectRef];
    this.setState(
      {
        pitch,
        pitchId,
      },
      () => {
        this.makeDraggableItems();
        this.removePitchBackground();
        this.addPitchBackground();
        this.addGrid();
        this.addWatermark();
      },
    );
  };

  addGrid = () => {
    const { height, width } = this.state.pitch;
    const { horizontalRows, verticalRows } = this.state.pitch.grid;
    const gridY = (fabricCanvas.getHeight() - height) / 2;
    const gridX = (fabricCanvas.getWidth() - width) / 2;
    const fabricWidth = fabricCanvas.getWidth() * 2;
    const fabricHeight = fabricCanvas.getHeight() * 2;
    const gridGroup = new fabric.Group([], {
      customProps: { ...items.GRID },
      width: fabricWidth,
      height: fabricHeight,
      top: gridY,
      left: gridX,
      originX: `center`,
      originY: `center`,
      evented: false,
      selectable: false,
      excludeFromExport: true,
    });
    const gridHeightSpacing = height / verticalRows;
    const gridWidthSpacing = width / horizontalRows;
    const gridLineWidth = 1;
    const gridLineWidthOffset = gridLineWidth / 2;
    const lineOption = {
      stroke: this.state.gridColor,
      strokeWidth: gridLineWidth,
      opacity: 0.8,
      strokeDashArray,
    };
    // Draw vertical lines
    for (let i = Math.ceil(width / gridWidthSpacing); i--;) {
      gridGroup.add(
        new fabric.Line(
          [
            gridWidthSpacing * i - gridLineWidthOffset,
            0,
            gridWidthSpacing * i - gridLineWidthOffset,
            height,
          ],
          lineOption,
        ),
      );
    }
    // Draw horizontal lines
    for (let i = Math.ceil(height / gridHeightSpacing); i--;) {
      gridGroup.add(
        new fabric.Line(
          [
            0,
            gridHeightSpacing * i - gridLineWidthOffset,
            width,
            gridHeightSpacing * i - gridLineWidthOffset,
          ],
          lineOption,
        ),
      );
    }

    this.setState({ gridObject: gridGroup }, () => {
      this.changeGridOpacity();
    });

    fabricCanvas
      .add(gridGroup)
      .moveTo(gridGroup, 1)
      .renderAll();
  };

  changeGridOpacity = () => {
    const { gridObject, gridShowing } = this.state;
    gridObject.set({ visible: gridShowing === true || false });
    fabricCanvas.renderAll();
  };

  handleChangePitchClick = (e, option) => {
    e.preventDefault();
    const pitchObjectRef = pitchIdentifier(option.value);
    const pitch = pitches[pitchObjectRef];

    if (pitch.children) {
      this.setState({ parentPitch: pitch });
    } else {
      this.changePitch(pitchObjectRef, option.value);
      const pitchIconSize = pitch.iconSize;
      this.setState({ iconSize: pitchIconSize }, () => {
        // We only want to load the setup when creating a new diagram, not when loading an old one hence why this can't be in the changePitch function
        if (`setup` in pitch) {
          this.loadSavedObjects(pitch.setup);
        }
      });
    }
  };

  removePitchBackground = () => {
    // Remove the old pitch background
    fabricCanvas.remove(this.state.pitchBackgroundObject).renderAll();
  };

  addPitchBackground = () => {
    // Add the new pitch background
    fabric.loadSVGFromString(
      this.state.pitch[this.state.theme.class].canvasImage,
      (objects, options) => {
        this.setState(
          {
            pitchBackgroundObject: fabric.util.groupSVGElements(
              objects,
              options,
            ),
          },
          () => {
            const { pitchBackgroundObject } = this.state;
            pitchBackgroundObject.selectable = false;
            pitchBackgroundObject.evented = false;
            pitchBackgroundObject.excludeFromExport = true;
            pitchBackgroundObject
              .scaleToWidth(this.state.pitch.width)
              .scaleToHeight(this.state.pitch.height);
            fabricCanvas
              .add(pitchBackgroundObject)
              // Needs to be moveTo(0) instead of sendToBack() otherwise the diagram background pattern added when saving the diagram overlaps the pitch background
              .moveTo(pitchBackgroundObject, 0)
              .centerObject(pitchBackgroundObject)
              .renderAll();
            pitchBackgroundObject.setCoords();
          },
        );
      },
    );
  };

  disableScroll = () => {
    fabricCanvas.allowTouchScrolling = false;
  };

  enableScroll = () => {
    fabricCanvas.allowTouchScrolling = true;
  };

  addEventListeners = () => {
    document.addEventListener(`keydown`, (e) => {
      this.handleDeletePress(e);
      this.handleEnterPress(e);
      this.handleEscPress(e);
      this.handleRotateShortcuts(e);
      this.handleMoveShortcuts(e);
    });

    // Bind the event listeners for the canvas
    fabricCanvas.on({
      "selection:created": this.handleObjectSelected,
      "selection:updated": this.handleObjectSelected,
      "object:moving": this.handleObjectMoving,
      "object:scaling": this.handleObjectScaling,
      "object:modified": this.handleObjectModified,
      "selection:cleared": this.clearCanvasSelection,
    });
  };

  handleGroupSelection = (group) => {
    // When selecting a group of objects we need to remove the grid & watermark from the active group as despite setting
    // evented: false and selectable: false on the them, they're still selectable when dragging to multiselect :(
    group.removeWithUpdate(this.state.gridObject);
    group.removeWithUpdate(this.state.watermarkObj);
    this.setState({ activeItem: group });
  };

  handleSingleObjectSelection = (object) => {
    switch (object.customProps.category) {
      case `line_anchor`:
        // If we select a line anchor we actually want the line to be the active object
        object = this.state.activeItem;
        // If we select a line anchor then we want to disable any tools (and in turn disabled drawing mode so that the user can drag an anchor straight after drawing a line)
        this.disableTool();
        break;

      case `lines`:
        this.lineCoordinatesForAnchors(object);
        this.repositionLineAnchors(object);
        this.showLineAnchors();
        break;

      case `paths`:
        this.repositionLineAnchors(object);
        this.showLineAnchors();
        this.showLineAnchorBend();
        break;

      case `text`: {
        this.hideLineAnchors();
        // On the teamsheet template it can be annoying to have to double click all the name textboxes to edit
        // Here we check to see if the value of the textbox is 'Name', if it is then we automatically enter edit mode ready for typing
        if (object.text === `Name`) {
          object.enterEditing();
        }
        break;
      }

      default:
        this.hideLineAnchors();
    }

    if (object.customProps.actions.text === true) {
      this.setState({ activeLabel: object.item(1).text });
    }

    // For some bizarre reason even though we set evented: false and selectable: false when adding the grid to the canvas
    // the grid is selectable when you drag to multiselect. This conditional makes sure that if it is selected we can't perform
    // actions on the grid e.g. deleting, moving etc.
    if (object.customProps.category !== `grid`) {
      this.setState({ activeItem: object });
    }
  };

  handleObjectSelected = (e) => {
    const activeObject = fabricCanvas._activeObject;
    const selectedItem = e.target;
    // If a single object selection
    if (activeObject.customProps) {
      this.handleSingleObjectSelection(selectedItem);
      // If a group selection
    } else {
      this.handleGroupSelection(activeObject);
    }
  };

  handleObjectMoving = (e) => {
    const activeObject = fabricCanvas._activeObject;
    // If not a group
    if (activeObject.customProps) {
      const selectedItem = e.target;
      switch (selectedItem.customProps.category) {
        case `line_anchor`:
          switch (selectedItem.customProps.type) {
            case `line_anchor_start`:
            case `line_anchor_end`:
              this.state.lineAnchorBend.visible = false;
            // no default
          }
          this.handleLineAnchorMove(selectedItem);
          break;
        case `lines`:
        case `paths`:
          this.state.lineAnchorStart.visible === true && this.hideLineAnchors();
          break;
        // no default
      }
    }

    // When moving an object that has already been painted on the canvas, show the canvas outline
    this.setState({ dragging: true, validDrop: true });
    this.disableScroll();
  };

  handleObjectModified = (e) => {
    const activeObject = fabricCanvas._activeObject;
    // If not a group
    if (activeObject.customProps) {
      const selectedItem = e.target;
      switch (selectedItem.customProps.category) {
        case `lines`:
          this.lineCoordinatesForAnchors(selectedItem);
          this.repositionLineAnchors(selectedItem);
          this.showLineAnchors();
          break;

        case `paths`: {
          this.redrawPathAndSetActive(selectedItem);
          this.showLineAnchors();
          this.showLineAnchorBend();
          break;
        }

        case `line_anchor`:
          if (this.state.activeItem.customProps.category === `paths`) {
            this.redrawPathAndSetActive(this.state.activeItem);
          }
          this.showLineAnchors();
          break;
        // no default
      }
    } else {
      const objectsInGroup = activeObject.getObjects();
      objectsInGroup.forEach((object) => {
        if (object.customProps.category === `paths`) {
          // If we modify a group which includes paths then we need to redraw all the paths and add them to the group
          this.redrawPath(object).then((newPath) => {
            activeObject.remove(object).addWithUpdate(newPath);
            fabricCanvas
              .remove(object)
              .add(newPath)
              .renderAll();
          });
        }
      });
    }

    // After moving an object that has already been painted on the canvas, remove the canvas outline
    this.setState({ dragging: false, validDrop: false });
    this.enableScroll();
  };

  clearCanvasSelection = () => {
    if (this.state.activeItem && this.state.activeItem.customProps) {
      switch (this.state.activeItem.customProps.category) {
        case `lines`:
        case `paths`:
          this.hideLineAnchors();
          break;
        // no default
      }
    }

    fabricCanvas.discardActiveObject().renderAll();
    this.removeLabelInputActiveState();
    this.setState({ activeItem: null, activeLabel: `` });
  };

  showLineAnchors = () => {
    if (this.state.lineAnchorStart) {
      this.state.lineAnchorStart.visible = true;
      this.state.lineAnchorEnd.visible = true;
      fabricCanvas
        .bringToFront(this.state.lineAnchorStart)
        .bringToFront(this.state.lineAnchorEnd)
        .renderAll();
    }
  };

  showLineAnchorBend = () => {
    if (this.state.lineAnchorBend) {
      this.state.lineAnchorBend.visible = true;
      fabricCanvas.bringToFront(this.state.lineAnchorBend).renderAll();
    }
  };

  hideLineAnchors = () => {
    if (this.state.lineAnchorStart) {
      this.state.lineAnchorStart.visible = false;
      this.state.lineAnchorEnd.visible = false;
      this.state.lineAnchorBend.visible = false;
    }
  };

  transformedPoint = (target) => {
    const points = [];
    const path = target.path;
    points.push(new fabric.Point(path[0][1], path[0][2]));
    points.push(new fabric.Point(path[1][3], path[1][4]));
    points.push(new fabric.Point(path[1][1], path[1][2]));
    const matrix = target.calcTransformMatrix();
    return points
      .map(
        p =>
          new fabric.Point(
            p.x - target.left - target.width / 2 - target.strokeWidth / 2,
            p.y - target.top - target.height / 2 - target.strokeWidth / 2,
          ),
      )
      .map(p => fabric.util.transformPoint(p, matrix));
  };

  redrawPathAndSetActive = (path) => {
    this.redrawPath(path).then((newPath) => {
      this.clearCanvasSelection();
      fabricCanvas
        .remove(path)
        .add(newPath)
        .setActiveObject(newPath)
        .renderAll();
    });
  };

  redrawPath = (oldLine) => {
    // When moving a path you have to redraw the whole thing
    // https://stackoverflow.com/questions/51406351/fabricjs-when-moving-a-path-the-path-object-doesnt-update
    const transformedPoints = this.transformedPoint(oldLine);
    const path = [[], []];
    path[0][0] = `M`;
    path[0][1] = transformedPoints[0].x;
    path[0][2] = transformedPoints[0].y;
    path[1][0] = `Q`;
    path[1][1] = transformedPoints[2].x;
    path[1][2] = transformedPoints[2].y;
    path[1][3] = transformedPoints[1].x;
    path[1][4] = transformedPoints[1].y;

    const newLine = this.selectLine(oldLine.customProps, path, oldLine.stroke);
    this.repositionLineAnchors(newLine);
    return Promise.resolve(newLine);
  };

  makeDraggableItems = () => {
    const draggableItems = [...document.querySelectorAll(`.tool-drag-svg`)];
    draggableItems.map((draggableItem) => {
      const draggableItemTrigger = draggableItem.querySelector(
        `.tool-drag-trigger`,
      );
      return this.makeItemDraggable(draggableItem, draggableItemTrigger);
    });
  };

  makeItemDraggable = (draggableItem, draggableItemTrigger) => {
    const that = this;
    const overlapThreshold = `100%`;
    let itemType;
    let startX;
    let startY;
    let triggerHeight;
    let triggerWidth;
    let originalIconHeight;
    let originalIconWidth;
    let iconHeight;
    let iconWidth;
    const canvas = document.getElementById(`canvas`);
    const draggingClass = `dragging`;
    const validDropClass = `valid-drop`;

    // From the Greensock Animation Platform, CDN links in footer.html
    Draggable.create(draggableItem, {
      trigger: draggableItemTrigger,

      onPress() {
        that.disableTool();
        itemType = uppercaseUnderscore(this.target.id);
        // When initially pressing we want to record the start co-ordinates
        triggerHeight = this.target.clientHeight;
        triggerWidth = this.target.clientWidth;
        startX = this.x;
        startY = this.y;
        if (items[itemType].sizes !== null) {
          iconHeight = items[itemType].sizes[that.state.iconSize].height;
          iconWidth = items[itemType].sizes[that.state.iconSize].width;
        }
      },

      onDragStart() {
        that.setState({ dragging: true });
        this.target.classList.add(draggingClass);
        if (items[itemType].sizes !== null) {
          // Get the default icon dimensions (have to use getBoundingClientRect instead of clientHeight and clientWidth because of Firefox bug https://bugzilla.mozilla.org/show_bug.cgi?id=874811)
          const originalIconDimensions = this.target.children[0].getBoundingClientRect();
          originalIconHeight = originalIconDimensions.height;
          originalIconWidth = originalIconDimensions.width;
          // When we start dragging we want to resize the icon to the size it will be when dropped on the canvas
          this.target.children[0].style.height = `${iconHeight}px`;
          this.target.children[0].style.width = `${iconWidth}px`;
        }
      },

      onDrag() {
        if (this.hitTest(canvas, overlapThreshold)) {
          that.setState({ validDrop: true });
          this.target.classList.add(validDropClass);
        } else {
          that.setState({ validDrop: false });
          this.target.classList.remove(validDropClass);
        }
      },

      onDragEnd(e) {
        if (that.state.validDrop === true) {
          const dropAreaRect = canvas.getBoundingClientRect();
          const targetRect = this.target.getBoundingClientRect();
          const targetX =
            targetRect.left - dropAreaRect.left + triggerWidth / 2;
          const targetY = targetRect.top - dropAreaRect.top + triggerHeight / 2;

          that.addDraggableCanvasItem(e, items[itemType], targetX, targetY);

          // Move the draggable item back to the sidebar
          TweenLite.to(this.target, 0, {
            x: startX,
            y: startY,
          });
        } else {
          // If it isn't in the drop area send it back to starting position
          TweenLite.to(this.target, 0.4, {
            x: startX,
            y: startY,
            ease: Back.easeOut,
          });
        }

        this.target.style.zIndex = 0; // Draggable adds a huge z-index to the dragged item which we want to remove after the dragging has ended
        that.setState({ dragging: false, validDrop: false });
        this.target.children[0].style.height = `${originalIconHeight}px`;
        this.target.children[0].style.width = `${originalIconWidth}px`;
        this.target.classList.remove(draggingClass);
      },
    });
  };

  addLineAnchors = () => {
    const lineAnchorStart = this.createLineAnchor(`line_anchor_start`);
    const lineAnchorEnd = this.createLineAnchor(`line_anchor_end`);
    const lineAnchorBend = this.createLineAnchorBend(`line_anchor_bend`);
    fabricCanvas.add(lineAnchorStart, lineAnchorEnd, lineAnchorBend);
    this.setState({ lineAnchorStart, lineAnchorEnd, lineAnchorBend });
  };

  createLineAnchor = anchorType =>
    new fabric.Rect({
      left: 0,
      top: 0,
      hasBorders: false,
      hasControls: false,
      originX: `center`,
      originY: `center`,
      height: 20,
      width: 20,
      strokeWidth,
      stroke: this.state.borderColor,
      fill: `rgba(255, 255, 255, 0.1)`,
      visible: false,
      excludeFromExport: true,
      customProps: {
        category: `line_anchor`,
        type: anchorType,
      },
    });

  createLineAnchorBend = anchorType =>
    new fabric.Circle({
      left: 0,
      top: 0,
      hasBorders: false,
      hasControls: false,
      originX: `center`,
      originY: `center`,
      radius: 10,
      strokeWidth,
      stroke: colors.BLUE,
      fill: `rgba(63, 149, 220, 0.5)`,
      visible: false,
      excludeFromExport: true,
      customProps: {
        category: `line_anchor`,
        type: anchorType,
      },
    });

  repositionLineAnchors = (line) => {
    if (line.customProps.category === `paths`) {
      this.state.lineAnchorStart
        .set({ left: line.path[0][1], top: line.path[0][2] })
        .setCoords();
      this.state.lineAnchorEnd
        .set({ left: line.path[1][3], top: line.path[1][4] })
        .setCoords();
      this.repositionLineAnchorBend(line);
    } else {
      this.state.lineAnchorStart
        .set({ left: line.x1, top: line.y1 })
        .setCoords();
      this.state.lineAnchorEnd.set({ left: line.x2, top: line.y2 }).setCoords();
    }
  };

  repositionLineAnchorBend = (line) => {
    // If the line is perfectly straight then we want to keep the bend anchor in the middle
    // But if it has had bend applied to it then we let it stay where it was dragged
    if (
      line.path[1][1] === line.path[1][3] &&
      line.path[1][2] === line.path[1][4]
    ) {
      const centerX = (line.path[0][1] + line.path[1][3]) / 2;
      const centerY = (line.path[0][2] + line.path[1][4]) / 2;
      this.state.lineAnchorBend
        .set({ left: centerX, top: centerY })
        .setCoords();
    } else {
      this.state.lineAnchorBend
        .set({ left: line.path[1][1], top: line.path[1][2] })
        .setCoords();
    }
  };

  handleChangeColorClick = (e, color, iconState) => {
    e.preventDefault();
    this.setState({ [iconState]: color });
  };

  loadedRotate = (item, rotateAngle) => {
    // When rotating an item that accepts text, we only want to rotate the shape, not the text too
    let itemToRotate;
    if (item.customProps.actions.text === true) {
      itemToRotate = item.item(0);
    } else {
      itemToRotate = item;
    }

    itemToRotate.rotate(rotateAngle).setCoords();
    item.set(`rotateAngle`, rotateAngle);
    fabricCanvas.renderAll();
  };

  rotateItem = (e, options) => {
    e.preventDefault();
    // When rotating an item that accepts text, we only want to rotate the shape, not the text too
    let itemToRotate;
    if (this.state.activeItem.customProps.actions.text === true) {
      itemToRotate = this.state.activeItem.item(0);
    } else {
      itemToRotate = this.state.activeItem;
    }

    const currentAngle = itemToRotate.angle;
    const rotateIncrement = 45;
    let rotateDirection = currentAngle + rotateIncrement;
    if (options && options.counterClockwise === true) {
      rotateDirection = currentAngle - rotateIncrement;
    }
    itemToRotate.rotate(rotateDirection).setCoords();
    // Even if we're rotating an item that accepts text, we want to set the rotateAngle on the parent item, not the 'itemToRotate'
    this.state.activeItem.set(`rotateAngle`, rotateDirection);
    fabricCanvas.renderAll();
  };

  handleDeletePress = (e) => {
    // Need the editingText check in here to avoid delete press removing the entire text object rather than individual letters
    if (
      this.state.activeItem !== null &&
      this.state.editingText === false &&
      this.state.labelInputActive === false
    ) {
      switch (e.keyCode) {
        // Backspace & Delete
        case 8:
        case 46:
          this.deleteItem(e);
          break;
        // no default
      }
    }
  };

  handleEnterPress = (e) => {
    if (
      this.state.activeItem !== null &&
      this.state.labelInputActive === true
    ) {
      switch (e.keyCode) {
        // If pressing enter (or return on iOS) we don't want to submit the form
        case 13:
          e.preventDefault();
          break;
        // no default
      }
    }
  };

  handleEscPress = (e) => {
    switch (e.keyCode) {
      // Escape
      case 27:
        e.preventDefault();
        if (this.state.activeTool !== null) {
          this.disableTool();
        }
        if (this.state.activeItem !== null) {
          this.clearCanvasSelection();
        }
        break;
      // no default
    }
  };

  handleRotateShortcuts = (e) => {
    if (
      this.state.activeItem !== null &&
      this.state.activeItem.customProps.actions.rotate === true &&
      this.state.labelInputActive === false
    ) {
      // If pressing alt and either right (39) or left (37)
      if (e.altKey && e.keyCode === 39) {
        this.rotateItem(e);
      } else if (e.altKey && e.keyCode === 37) {
        this.rotateItem(e, { counterClockwise: true });
      }
    }
  };

  handleMoveShortcuts = (e) => {
    if (
      this.state.activeItem !== null &&
      this.state.labelInputActive === false
    ) {
      // If the alt key is pressed we don't want to nudge as will run alongside rotate shortcut
      if (e.altKey === false) {
        let nudgeAmount;
        if (e.shiftKey === true) {
          nudgeAmount = 10;
        } else {
          nudgeAmount = 1;
        }

        switch (e.keyCode) {
          // Left
          case 37:
            e.preventDefault();
            this.state.activeItem
              .set(`left`, this.state.activeItem.left - nudgeAmount)
              .setCoords();
            break;
          // Up
          case 38:
            e.preventDefault();
            this.state.activeItem
              .set(`top`, this.state.activeItem.top - nudgeAmount)
              .setCoords();
            break;
          // Right
          case 39:
            e.preventDefault();
            this.state.activeItem
              .set(`left`, this.state.activeItem.left + nudgeAmount)
              .setCoords();
            break;
          // Down
          case 40:
            e.preventDefault();
            this.state.activeItem
              .set(`top`, this.state.activeItem.top + nudgeAmount)
              .setCoords();
            break;
          // no default
        }

        // If we move a line with the keyboard we also need to update the anchors
        if (this.state.activeItem.customProps) {
          switch (this.state.activeItem.customProps.category) {
            case `lines`:
            case `paths`:
              // Trigger object modified to update coordinates before repositioning line anchors
              fabricCanvas.trigger(`object:modified`, {
                target: this.state.activeItem,
              });
              this.repositionLineAnchors(this.state.activeItem);
            // no default
          }
        }

        fabricCanvas.renderAll();
      }
    }
  };

  deleteItem = (e, item) => {
    if (e) {
      e.preventDefault();
    }
    // If a specific item is specified then delete that
    if (item) {
      fabricCanvas.remove(item);
    } else {
      // If no specific item is specified then we either delete the active item or the active group
      const activeObject = fabricCanvas._activeObject;
      if (activeObject.customProps) {
        fabricCanvas.remove(this.state.activeItem);
      } else {
        const objectsInGroup = activeObject.getObjects();
        fabricCanvas.discardActiveObject();
        objectsInGroup.forEach((object) => {
          fabricCanvas.remove(object);
        });
      }
    }
    this.hideLineAnchors();
  };

  duplicateItem = (e) => {
    e.preventDefault();
    const duplicate = fabric.util.object.clone(fabricCanvas._activeObject);
    duplicate
      .set({
        top: this.state.activeItem.top + 5,
        left: this.state.activeItem.left + 5,
        evented: true,
      })
      .setCoords();
    fabricCanvas
      .discardActiveObject()
      .add(duplicate)
      .setActiveObject(duplicate)
      .renderAll();
  };

  handleLabelChange = (e) => {
    this.setState({ activeLabel: e.target.value });
    this.state.activeItem.item(1).set({
      text: e.target.value,
    });
    fabricCanvas.renderAll();
  };

  handleLabelFocus = () => {
    this.setState({ labelInputActive: true });
  };

  removeLabelInputActiveState = () => {
    this.setState({ labelInputActive: false });
  };

  addDraggableItemToCanvas = (newItem, x, y) => {
    newItem.set({
      borderColor: this.state.borderColor,
      left: x,
      top: y,
      originX: `center`,
      originY: `center`,
    });
    if (newItem.customProps.type !== `text`) {
      newItem.hasControls = false;
    }

    fabricCanvas
      .add(newItem)
      .setActiveObject(newItem)
      .renderAll();
    newItem.setCoords();

    if (newItem.customProps.type === `text`) {
      newItem.enterEditing();
    }
  };

  addDraggableCanvasItem = (
    e,
    item,
    x,
    y,
    loadedIconFill,
    loadedRotateAngle,
    loadedPlayerText,
  ) => {
    // We do this as this addDraggableCanvasItem function is when dropping items on the canvas (an event is fired)
    // And when initially loading a saved diagram (no event is fired so we pass null)
    if (e) {
      e.preventDefault();
    }

    switch (item.category) {
      case `equipment`: {
        let iconFill = this.state.equipmentIconColor;
        if (loadedIconFill) {
          iconFill = loadedIconFill;
        }

        drawEquipment(item, iconFill, this.state.iconSize).then((newItem) => {
          if (loadedRotateAngle) {
            this.loadedRotate(newItem, loadedRotateAngle);
          }
          this.addDraggableItemToCanvas(newItem, x, y);
        });
        break;
      }

      case `players`: {
        let iconFill = this.state.playerIconColor;
        if (loadedIconFill) {
          iconFill = loadedIconFill;
        }

        let label = item.text;
        if (loadedPlayerText) {
          label = loadedPlayerText;
        }

        drawPlayer(
          item,
          iconFill,
          this.state.theme.playerInnerFill,
          this.state.iconSize,
          label,
        ).then((newItem) => {
          if (loadedRotateAngle) {
            this.loadedRotate(newItem, loadedRotateAngle);
          }
          this.addDraggableItemToCanvas(newItem, x, y);
        });
        break;
      }

      case `text`: {
        const newItem = this.drawTextBox(item);
        if (loadedRotateAngle) {
          this.loadedRotate(newItem, loadedRotateAngle);
        }
        this.addDraggableItemToCanvas(newItem, x, y);
        break;
      }
      // no default
    }
  };

  drawRectangle = (item, x, y, fill) =>
    new Rectangle({
      fill,
      borderColor: this.state.borderColor,
      cornerColor: this.state.borderColor,
      cornerSize: shapeCornerSize,
      left: x,
      top: y,
      customProps: item,
    });

  drawCircle = (item, x, y, fill) =>
    new Circle({
      fill,
      borderColor: this.state.borderColor,
      cornerColor: this.state.borderColor,
      cornerSize: shapeCornerSize,
      left: x,
      top: y,
      customProps: item,
    });

  addShapeToCanvas = (shape) => {
    fabricCanvas
      .add(shape)
      .moveTo(shape, 1) // Send shapes to the back (but above the pitch background(0))
      .renderAll();

    if (shape.customProps.type === `square`) {
      shape.setControlsVisibility({
        mt: false, // middle top
        mb: false, // middle bottom
        ml: false, // middle left
        mr: false, // middle right
      });
    }
  };

  selectShape = (item, x, y, fill) => {
    switch (item.type) {
      case `square`:
      case `rectangle`:
        return this.drawRectangle(item, x, y, fill);
      case `circle`:
        return this.drawCircle(item, x, y, fill);
      // no default
    }
    return false;
  };

  addShape = (item) => {
    let shape;
    let isDown;
    let startPoint;

    fabricCanvas.on(`mouse:down`, (options) => {
      this.disableScroll();
      isDown = true;
      startPoint = fabricCanvas.getPointer(options.e);
      shape = this.selectShape(
        item,
        startPoint.x,
        startPoint.y,
        this.state.shapeIconColor,
      );
      shape.selectable = false; // This is needed to prevent newly added lines from being dragged if drawing a line right next to them
      this.addShapeToCanvas(shape);
    });

    fabricCanvas.on(`mouse:move`, (options) => {
      if (this.state.validDrop === false) {
        this.setState({ validDrop: true });
      }
      if (!isDown) return;

      const pointer = fabricCanvas.getPointer(options.e);
      const shapeWidth = pointer.x - startPoint.x;
      const shapeHeight = pointer.y - startPoint.y;
      let squareValue = shapeWidth;

      // Handle negative values (if you attempt to create a shape with the origin bottom right instead of top left)
      if (startPoint.x > pointer.x) {
        shape.set({ left: Math.abs(pointer.x) });
        squareValue = shapeWidth;
      }
      if (startPoint.y > pointer.y) {
        shape.set({ top: Math.abs(pointer.y) });
        squareValue = shapeHeight;
      }

      switch (item.type) {
        case `square`:
          if (startPoint.x > pointer.x && startPoint.y > pointer.y) {
            if (Math.abs(shapeWidth) > Math.abs(shapeHeight)) {
              squareValue = shapeWidth;
              shape.set({
                height: Math.abs(squareValue),
                width: Math.abs(squareValue),
              });
            } else {
              squareValue = shapeHeight;
              shape.set({
                height: Math.abs(squareValue),
                width: Math.abs(squareValue),
              });
            }
          } else {
            shape.set({
              height: Math.abs(squareValue),
              width: Math.abs(squareValue),
            });
          }
          break;
        case `rectangle`:
          shape.set({
            height: Math.abs(shapeHeight),
            width: Math.abs(shapeWidth),
          });
          break;
        case `circle`:
          shape.set({
            ry: Math.abs(shapeHeight / 2),
            rx: Math.abs(shapeWidth / 2),
          });
          break;
        // no default
      }

      shape.setCoords();
      fabricCanvas.renderAll();
    });

    fabricCanvas.on(`mouse:up`, (options) => {
      isDown = false;
      const endPoint = fabricCanvas.getPointer(options.e);
      this.drawOrToggleTool(startPoint, endPoint, shape);
    });

    fabricCanvas.on(`mouse:out`, () => {
      this.setState({ validDrop: false });
    });
  };

  handleTextEditingEntered = () => {
    this.setState({ editingText: true });
  };

  handleTextEditingExited = () => {
    this.setState({ editingText: false });
  };

  drawTextBox = (item) => {
    const textbox = new Text(``, {
      fill: this.state.theme.textColor,
      borderColor: this.state.borderColor,
      cornerColor: this.state.borderColor,
      cursorColor: this.state.borderColor,
      editingBorderColor: this.state.borderColor,
      customProps: item,
    });

    textbox.setControlsVisibility({
      tl: false, // top left
      tr: false, // top right
      mt: false, // middle top
      mb: false, // middle bottom
      bl: false, // bottom left
      br: false, // bottom right
    });

    textbox.on({
      "editing:entered": this.handleTextEditingEntered,
      "editing:exited": this.handleTextEditingExited,
    });

    return textbox;
  };

  toggleTool = (e, item) => {
    e && e.preventDefault();

    // Disable any active tools
    if (this.state.activeTool === item.type) {
      this.disableTool();
      // Enable the clicked tool
    } else {
      this.setState({ activeTool: item.type, dragging: true }, () => {
        this.clearCanvasSelection();
        // We need to disable before enabling again to prevent drawing multiple shapes if the user clicks from one tool to another without drawing a shape
        this.disableDrawingMode();
        this.enableDrawingMode();

        switch (item.category) {
          case `shapes`:
            this.addShape(item);
            break;

          case `lines`:
          case `paths`:
            this.addLine(item);
            break;
          // no default
        }
      });
    }
  };

  disableTool = () => {
    this.setState(
      { activeTool: null, dragging: false, validDrop: false },
      () => {
        this.disableDrawingMode();
      },
    );
  };

  disableDrawingMode = () => {
    this.enableScroll();
    fabricCanvas.selection = true;
    fabricCanvas.forEachObject((object, i) => {
      // This is to prevent the pitch background from being set to selectable (it is 0 in the object array)
      if (i > 0) {
        object.selectable = true;
      }
    });
    fabricCanvas.defaultCursor = `default`;
    fabricCanvas.hoverCursor = `move`;
    // Remove event listeners
    fabricCanvas
      .off(`mouse:down`)
      .off(`mouse:move`)
      .off(`mouse:up`)
      .off(`mouse:out`);
  };

  enableDrawingMode = () => {
    fabricCanvas.selection = false;
    fabricCanvas.forEachObject((object) => {
      object.selectable = false;
    });
    // Allow line anchors to be draggable while in drawing mode
    this.state.lineAnchorStart.selectable = true;
    this.state.lineAnchorEnd.selectable = true;
    this.state.lineAnchorBend.selectable = true;
    fabricCanvas.defaultCursor = `crosshair`;
    fabricCanvas.hoverCursor = `crosshair`;
    this.state.lineAnchorStart.hoverCursor = `move`;
    this.state.lineAnchorEnd.hoverCursor = `move`;
    this.state.lineAnchorBend.hoverCursor = `move`;
  };

  drawLineWithArrow = (item, path, stroke, legacyLine) => {
    const options = {
      customProps: item,
      strokeWidth,
      stroke,
      padding: objectPadding,
    };
    if (legacyLine) return new LegacyLineWithArrow(path, options);
    return new LegacyLineWithArrow(path, options);
  };

  drawDashedLineWithArrow = (item, path, stroke, legacyLine) => {
    const options = {
      customProps: item,
      strokeWidth,
      stroke,
      strokeDashArray,
      padding: objectPadding,
    };
    if (legacyLine) return new LegacyLineWithArrow(path, options);
    return new LegacyLineWithArrow(path, options);
  };

  drawDashedLine = (item, path, stroke, legacyLine) => {
    const options = {
      customProps: item,
      strokeWidth,
      stroke,
      strokeDashArray,
      padding: objectPadding,
    };
    if (legacyLine) return new LegacyLine(path, options);
    return new Line(path, options);
  };

  drawWavyLineWithArrow = (item, points, stroke) =>
    new WavyLine(points, {
      customProps: item,
      strokeWidth,
      stroke,
      padding: objectPadding,
    });

  selectLine = (item, points, stroke, legacyLine) => {
    switch (item.type) {
      case `line_with_arrow`:
        return this.drawLineWithArrow(item, points, stroke, legacyLine);
      case `dashed_line_with_arrow`:
        return this.drawDashedLineWithArrow(item, points, stroke, legacyLine);
      case `wavy_line_with_arrow`:
        return this.drawWavyLineWithArrow(item, points, stroke, legacyLine);
      case `dashed_line`:
        return this.drawDashedLine(item, points, stroke, legacyLine);
      // no default
    }
    return null;
  };

  lineStartPoint = (category, pointer) => {
    const { x, y } = pointer;
    let path;
    if (category === `paths`) {
      path = [[], []];
      path[0][0] = `M`;
      path[0][1] = x;
      path[0][2] = y;
      path[1][0] = `Q`;
      path[1][1] = x;
      path[1][2] = y;
      path[1][3] = x;
      path[1][4] = y;
    } else {
      path = [x, y, x, y];
    }
    return path;
  };

  lineEndPoint = (line, pointer) => {
    const { x, y } = pointer;
    if (line.customProps.category === `paths`) {
      line.path[1][1] = x;
      line.path[1][2] = y;
      line.path[1][3] = x;
      line.path[1][4] = y;
    } else {
      line.set({ x2: x, y2: y });
    }
  };

  addLine = (item) => {
    let line;
    let isDown;
    let startPoint;

    fabricCanvas.on(`mouse:down`, (options) => {
      this.hideLineAnchors();
      this.disableScroll();
      isDown = true;
      startPoint = fabricCanvas.getPointer(options.e);
      const path = this.lineStartPoint(item.category, startPoint);
      line = this.selectLine(item, path, this.state.lineIconColor);
      line.selectable = false; // This is needed to prevent newly added lines from being dragged if drawing a line right next to them
      fabricCanvas.add(line).renderAll();
    });

    fabricCanvas.on(`mouse:move`, (options) => {
      if (this.state.validDrop === false) {
        this.setState({ validDrop: true });
      }
      if (!isDown) return;
      const pointer = fabricCanvas.getPointer(options.e);
      this.lineEndPoint(line, pointer);

      if (line.customProps.category === `paths`) {
        const dims = line._parseDimensions();
        line.set({ height: dims.height, width: dims.width }).setCoords();
        line.pathOffset.x = line.width / 2 + line.left;
        line.pathOffset.y = line.height / 2 + line.top;
      } else {
        line.set({ x2: pointer.x, y2: pointer.y }).setCoords();
      }

      line.setCoords();
      this.state.lineAnchorEnd.set({ left: pointer.x, top: pointer.y });
      fabricCanvas.renderAll();
    });

    fabricCanvas.on(`mouse:up`, (options) => {
      isDown = false;
      const endPoint = fabricCanvas.getPointer(options.e);
      if (item.category === `paths`) {
        this.drawOrTogglePath(startPoint, endPoint, line);
      } else {
        line.set({ x2: endPoint.x, y2: endPoint.y }).setCoords();
        this.drawOrToggleTool(startPoint, endPoint, line);
      }
    });

    fabricCanvas.on(`mouse:out`, () => {
      this.setState({ validDrop: false });
    });
  };

  drawOrToggleTool = (startPoint, endPoint, item) => {
    if (startPoint.x === endPoint.x && startPoint.y === endPoint.y) {
      this.deleteItem(null, item);
      this.disableTool();
    } else {
      item.setCoords();
      fabricCanvas.setActiveObject(item).renderAll();
    }
  };

  drawOrTogglePath = (startPoint, endPoint, path) => {
    if (startPoint.x === endPoint.x && startPoint.y === endPoint.y) {
      this.deleteItem(null, path);
      this.disableTool();
    } else {
      this.redrawPathAndSetActive(path);
    }
  };

  lineCoordinatesForAnchors = (line) => {
    const height = line.height / 2;
    const width = line.width / 2;
    const oldCenterX = (line.x1 + line.x2) / 2;
    const oldCenterY = (line.y1 + line.y2) / 2;
    const deltaX = line.left - oldCenterX;
    const deltaY = line.top - oldCenterY;

    line
      .set({
        x1: line.x1 + deltaX + width,
        y1: line.y1 + deltaY + height,
        x2: line.x2 + deltaX + width,
        y2: line.y2 + deltaY + height,
      })
      .setCoords();
  };

  handleLineAnchorMove = (target) => {
    const line = this.state.activeItem;
    switch (target.customProps.category) {
      // Moving the line anchors
      case `line_anchor`:
        switch (target.customProps.type) {
          case `line_anchor_start`:
            if (line.customProps.category === `paths`) {
              line.path[0][1] = target.left;
              line.path[0][2] = target.top;
            } else {
              line.set({ x1: target.left, y1: target.top });
            }
            line.setCoords();
            break;

          case `line_anchor_end`:
            if (line.customProps.category === `paths`) {
              // If the line is perfectly straight then we want to keep the quadratic value the same as the end point to avoid bending it
              // But if it has had bend applied to it then the two can be treated separately
              if (
                line.path[1][1] === line.path[1][3] &&
                line.path[1][2] === line.path[1][4]
              ) {
                line.path[1][1] = target.left;
                line.path[1][2] = target.top;
              }
              line.path[1][3] = target.left;
              line.path[1][4] = target.top;
            } else {
              line.set({ x2: target.left, y2: target.top });
            }
            line.setCoords();
            break;

          case `line_anchor_bend`:
            line.path[1][1] = target.left;
            line.path[1][2] = target.top;
            line.setCoords();
            break;
          // no default
        }
      // no default
    }
    fabricCanvas.renderAll();
  };

  clearCanvas = (e) => {
    e.preventDefault();
    fabricCanvas.clear();
    this.addPitchBackground();
    this.addWatermark();
    this.addGrid();
    this.addLineAnchors();
    this.closeModal(`clearAllModalOpen`);
  };

  handleToggleGridClick = (e) => {
    e.preventDefault();
    this.setState({ gridShowing: !this.state.gridShowing }, () => {
      this.changeGridOpacity();
    });
  };

  editingTextChange = (textItem) => {
    // If you were editing the text when you clicked on the text align toggle then we need to exit and then re-enter as there is a Fabric bug that causes delays in text editing without it
    if (this.state.editingText) {
      textItem.exitEditing().enterEditing();
    }
  };

  handleTextAlignChange = (e) => {
    const textItem = this.state.activeItem;
    textItem.set({ textAlign: e.target.value });
    // Have to re-set the active item here with the new text align value to trigger the changing of the active radio button
    this.setState({ activeItem: textItem });
    this.editingTextChange(textItem);
    fabricCanvas.renderAll();
  };

  handleFontSizeChange = (selectedOption) => {
    const textItem = this.state.activeItem;
    textItem.set({ fontSize: selectedOption.value });
    // Have to re-set the active item here with the new font size value to trigger the changing of the select dropdown
    this.setState({ activeItem: textItem });
    this.editingTextChange(textItem);
    fabricCanvas.renderAll();
  };

  checkIfSaved = (e) => {
    // Clear any selected items here so that we aren't saving images with active borders
    this.clearCanvasSelection();
    if (this.state.content.title.length) {
      this.saveContent(e);
    } else {
      this.openModal(e, `saveModalOpen`);
    }
  };

  openModal = (e, modal) => {
    e.preventDefault();
    this.setState({ [modal]: true });
  };

  closeModal = (modal) => {
    this.setState({ [modal]: false });
  };

  handleTitleChange = (e) => {
    const title = e.target.value;
    this.setState({ title });
  };

  handleParentPitchBackClick = () => {
    this.setState({ parentPitch: null });
  };

  handlePitchBackClick = () => {
    this.setState({ theme: null, themeId: 0 });
  };

  saveContent = (e) => {
    e.preventDefault();
    this.setState({ loading: true });
    const savedCanvas = fabricCanvas.toObject();
    const content = { ...this.state.content };
    content.title = this.state.title;
    content.pitch_theme.id = this.state.themeId;
    content.pitch_image.id = this.state.pitchId;
    content.pitch_data = savedCanvas;
    fabricCanvas.bringToFront(this.state.watermarkObj);
    this.props.actions.saveDiagram(content)
      .then((savedDiagram) => {
        this.canvasToImage(savedDiagram.id, savedDiagram.slug);
      });
  };

  canvasToImage = (diagramId, slug) => {
    // The background pattern is rendered via CSS initially to allow us to render the whole page with that pattern.
    // However, when saving the image we want to add it to the canvas so that it is saved as part of the image

    const src = `${staticUrl()}assets/images/raster/diagram-creator/diagram-creator-bg-pattern-${this.state.theme.class}.png`;
    fabricCanvas.setBackgroundColor(
      {
        source: src,
        repeat: `repeat`,
        crossOrigin: `anonymous`,
      }, () => {
      this.state.gridObject.visible = false; // Hide the grid before we save the canvas to image

      // Needs to be moveTo(0) instead of sendToBack() otherwise the diagram background pattern added when saving the diagram overlaps the pitch background
      // We do this again before saving the image as sometimes the pitch background is hidden for some reason
      fabricCanvas.moveTo(this.state.pitchBackgroundObject, 0).renderAll();
      fabricCanvas.crossOrigin = `anonymous`;
      const canvasImage = fabricCanvas.toDataURL(`image/jpeg`, 1.0);
      const obj = {
        file: canvasImage,
        id: diagramId,
      };
      this.props.actions.saveDiagramImage(obj).then(() => {
        this.setState({ loading: false });
        window.location.href = slug;
      });
    });
  };

  render() {
    const {
      loading,
      theme,
      pitch,
      parentPitch,
      title,
      shapeIconColor,
      equipmentIconColor,
      playerIconColor,
      lineIconColor,
      activeItem,
      activeLabel,
      clearAllModalOpen,
      saveModalOpen,
      dragging,
      validDrop,
      activeTool,
      gridShowing,
    } = this.state;
    const { metadataOptions, organisation } = this.props;

    // If a theme has been selected then add a class to the body
    let themeClass;
    if (theme && `class` in theme) {
      themeClass = theme.class;
    }

    const pageClass = classnames(
      `bg-white diagram-creator-page ${themeClass}`,
      {
        dragging,
        "valid-drop": validDrop,
      },
    );

    return (
      <form className={pageClass}>
        <h1 className="visuallyhidden">Diagram Creator</h1>

        {pitch === null && (
          <DiagramCreatorOptions
            metadataOptions={metadataOptions}
            changeTheme={this.handleChangeThemeClick}
            changePitch={this.handleChangePitchClick}
            theme={theme}
            parentPitch={parentPitch}
            onParentPitchBackClick={this.handleParentPitchBackClick}
            onPitchBackClick={this.handlePitchBackClick}
          />
        )}

        <DiagramCreatorHeader
          loading={loading}
          contentTitle={title}
          onTitleChange={this.handleTitleChange}
          onSaveClick={this.checkIfSaved}
          activeItem={activeItem}
          onDeleteItemClick={this.deleteItem}
          onDuplicateItemClick={this.duplicateItem}
          onRotateItemClick={this.rotateItem}
          currentLabel={activeLabel}
          onLabelChange={this.handleLabelChange}
          onLabelFocus={this.handleLabelFocus}
          inputRef={(el) => {
            this.labelInput = el;
          }}
          onClearCanvasClick={e => this.openModal(e, `clearAllModalOpen`)}
          onToggleGridClick={this.handleToggleGridClick}
          gridShowing={gridShowing}
          onTextAlignChange={this.handleTextAlignChange}
          onFontSizeChange={this.handleFontSizeChange}
        />

        <PageContainer>
          {theme && pitch && (
            <section className="diagram-creator-sidebar">
              <DiagramCreatorShapes
                activeTool={activeTool}
                toggleShape={this.toggleTool}
                activeColor={shapeIconColor}
                changeColor={(e, color) =>
                  this.handleChangeColorClick(e, color, `shapeIconColor`)
                }
              />
              <DiagramCreatorEquipment
                activeColor={equipmentIconColor}
                changeColor={(e, color) =>
                  this.handleChangeColorClick(e, color, `equipmentIconColor`)
                }
              />
              <DiagramCreatorPlayers
                activeColor={playerIconColor}
                changeColor={(e, color) =>
                  this.handleChangeColorClick(e, color, `playerIconColor`)
                }
              />
              <DiagramCreatorLines
                activeTool={activeTool}
                toggleLine={this.toggleTool}
                activeColor={lineIconColor}
                changeColor={(e, color) =>
                  this.handleChangeColorClick(e, color, `lineIconColor`)
                }
              />
              <DiagramCreatorText />
            </section>
          )}

          <section className="diagram-creator-canvas-outer">
            <div className="diagram-creator-canvas-container">
              <div
                id="diagram-creator-canvas"
                className="diagram-creator-canvas"
              >
                <canvas id="canvas" />
              </div>
            </div>
          </section>

          <ModalContainer
            isOpen={clearAllModalOpen}
            closeModal={() => this.closeModal(`clearAllModalOpen`)}
            label="Clear all items modal"
          >
            <ConfirmModal
              title="Are you sure?"
              confirmText="Clear all"
              onConfirmClick={this.clearCanvas}
              onCloseClick={() => this.closeModal(`clearAllModalOpen`)}
            >
              <p>Are you sure you want to clear all items from the diagram?</p>
            </ConfirmModal>
          </ModalContainer>

          <ModalContainer
            isOpen={saveModalOpen}
            closeModal={() => this.closeModal(`saveModalOpen`)}
            label="Save Diagram modal"
          >
            <CreateNewModal
              contentType="diagram"
              onSubmitClick={this.saveContent}
              onCloseClick={() => this.closeModal(`saveModalOpen`)}
              onInputChange={this.handleTitleChange}
              inputPlaceholder="Diagram title e.g. Playing out from the back"
              inputValue={title}
              inputMaxLength={200}
              loading={loading}
            >
              <p>
                Give your diagram a descriptive title to make finding it easier.
              </p>
            </CreateNewModal>
          </ModalContainer>
        </PageContainer>
      </form>
    );
  }
}

DiagramCreatorPage.propTypes = {
  diagram: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  metadataOptions: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  organisation: PropTypes.object.isRequired,
  location: PropTypes.object,
};

function mapStateToProps(state, ownProps) {
  let content;
  const contentId = ownProps.params.id; // Get the content ID from the URL

  // If the URL contains a content ID then we set content to be response from the queryapi which is fired in routes.js
  if (contentId || state.diagram.id) {
    content = state.diagram;
  } else {
    content = {
      title: ``,
      pitch_image: { id: 0 },
      pitch_theme: { id: 0 },
      pitch_data: [],
    };
  }

  const metadataFormattedForInput = {};
  Object.keys(state.metadata).forEach((key) => {
    metadataFormattedForInput[key] = state.metadata[key].map(option => ({
      value: option.id,
      label: option.name,
      description: option.description,
    }));
  });

  return {
    diagram: content,
    metadataOptions: metadataFormattedForInput,
    organisation: state.organisation,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Object.assign({}, diagramActions), dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DiagramCreatorPage);



// WEBPACK FOOTER //
// ./library/app/components/Diagram/DiagramCreatorPage.js